import { useEffect, useState } from 'react';

import { datadogRum } from 'root/datadog/datadogRum';

import { AggregateSaleData } from '../state/AggregateSaleData';

export function useAggregateWorkorder(workorderID?: string) {
  const accountID = window.merchantos.account.id;
  const [aggregateWorkorder, setAggregateWorkorder] = useState<AggregateSaleData | null>(null);
  const [loading, setLoading] = useState<boolean>(!!workorderID);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // handle missing workorderID clear state and stop loading
    if (!workorderID) {
      setAggregateWorkorder(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(
      `/API/V3/Account/${accountID}/DisplayTemplate/Workorder/${workorderID}.json?load_relations=all`
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Transform workorder data to match AggregateSaleData structure
        const workorderData = data?.Workorder;

        // Transform WorkorderItems to SaleLines format
        const workorderItems = workorderData?.WorkorderItems?.WorkorderItem;
        let saleLines: SaleLine[] = [];
        if (Array.isArray(workorderItems)) {
          // Filter out child workorder items that are already included in parent's SaleLine array
          const filteredItems = workorderItems.filter((item) => {
            // Keep items that don't have a parent (workorderItemParentID is 0 or undefined)
            return (
              !item.workorderItemParentID ||
              item.workorderItemParentID === '0' ||
              item.workorderItemParentID === 0
            );
          });

          saleLines = [];
          filteredItems.forEach((item) => {
            const saleLineData = item.SaleLine;
            if (Array.isArray(saleLineData)) {
              saleLineData.forEach((saleLine) => {
                // Only attach the main Item to the primary SaleLine (not item fees)
                if (saleLine && !saleLine.Item && item.Item && saleLine.lineType !== 'item_fee') {
                  saleLine.Item = item.Item;
                }
                // Copy note from WorkorderItem to SaleLine if it exists
                if (item.note && item.note !== '') {
                  saleLine.Note = {
                    note: item.note,
                  };
                }
                // If Discount object is missing but we have discount data, construct it
                if (!saleLine.Discount && (saleLine.calcLineDiscount || saleLine.discountPercent)) {
                  saleLine.Discount = {
                    discountID: saleLine.discountID || '',
                    name: saleLine.discountName || 'Discount',
                    discountAmount: saleLine.discountAmount || '0',
                  };
                }

                saleLines.push(saleLine);
              });
            } else if (saleLineData) {
              if (!saleLineData.Item && item.Item) {
                saleLineData.Item = item.Item;
              }
              // Copy note from WorkorderItem to SaleLine if it exists
              if (item.note && item.note !== '') {
                saleLineData.Note = {
                  note: item.note,
                };
              }
              if (
                !saleLineData.Discount &&
                (saleLineData.calcLineDiscount || saleLineData.discountPercent)
              ) {
                saleLineData.Discount = {
                  discountID: saleLineData.discountID || '',
                  name: saleLineData.discountName || 'Discount',
                  discountAmount: saleLineData.discountAmount || '0',
                };
              }
              saleLines.push(saleLineData);
            }
          });
        } else if (workorderItems?.SaleLine) {
          const saleLineData = workorderItems.SaleLine;

          if (Array.isArray(saleLineData)) {
            saleLines = saleLineData.map((saleLine) => {
              if (saleLine && !saleLine.Item && workorderItems.Item) {
                saleLine.Item = workorderItems.Item;
              }
              // Copy note from WorkorderItem to SaleLine if it exists
              if (workorderItems.note && workorderItems.note !== '') {
                saleLine.Note = {
                  note: workorderItems.note,
                };
              }
              if (!saleLine.Discount && (saleLine.calcLineDiscount || saleLine.discountPercent)) {
                saleLine.Discount = {
                  discountID: saleLine.discountID || '',
                  name: saleLine.discountName || 'Discount',
                  discountAmount: saleLine.discountAmount || '0',
                };
              }
              return saleLine;
            });
          } else {
            if (saleLineData && !saleLineData.Item && workorderItems.Item) {
              saleLineData.Item = workorderItems.Item;
            }
            // Copy note from WorkorderItem to SaleLine if it exists
            if (workorderItems.note && workorderItems.note !== '') {
              saleLineData.Note = {
                note: workorderItems.note,
              };
            }
            if (
              !saleLineData.Discount &&
              (saleLineData.calcLineDiscount || saleLineData.discountPercent)
            ) {
              saleLineData.Discount = {
                discountID: saleLineData.discountID || '',
                name: saleLineData.discountName || 'Discount',
                discountAmount: saleLineData.discountAmount || '0',
              };
            }
            saleLines = [saleLineData];
          }
        }

        // Process WorkorderLines (labor/work) and add them to saleLines
        if (workorderData?.WorkorderLines?.WorkorderLine) {
          const workorderLines = Array.isArray(workorderData.WorkorderLines.WorkorderLine)
            ? workorderData.WorkorderLines.WorkorderLine
            : [workorderData.WorkorderLines.WorkorderLine];

          workorderLines.forEach((laborLine: WorkOrderLine) => {
            if (laborLine.SaleLine) {
              // Create a labor item description from the note
              const laborDescription = laborLine.note || 'Labor';

              const laborItem = {
                description: laborDescription,
                itemType: 'labor',
                Prices: {
                  ItemPrice: [
                    {
                      amount: laborLine.unitPriceOverride || laborLine.SaleLine.unitPrice || '0',
                    },
                  ],
                },
              };

              // Attach the labor item to the SaleLine
              laborLine.SaleLine.Item = laborItem;

              // Discount
              if (laborLine.Discount) {
                laborLine.SaleLine.Discount = laborLine.Discount;
              }

              // Add the labor SaleLine to the saleLines array
              saleLines.push(laborLine.SaleLine);
            }
          });
        }

        const aggregateWorkorderData: AggregateSaleData = {
          Sale: {
            saleID: workorderID, // Use workorderID as saleID for compatibility
            registerID: workorderData?.Employee?.lastRegisterID ?? '', // Use employee's last registerID as it does not exist on workorder
            displayableSubtotal: (
              parseFloat(workorderData?.m?.parts ?? '0') +
              parseFloat(workorderData?.m?.labor ?? '0')
            ).toFixed(2),
            calcItemFees: workorderData?.m?.itemFeesSubtotal ?? '0',
            calcDiscount: workorderData?.m?.discount ?? '0',
            calcTotal: workorderData?.m?.total ?? '0',
            employeeID: workorderData?.employeeID ?? '',
            Customer: workorderData?.Customer
              ? {
                  ...workorderData.Customer,
                  Layaways: undefined,
                  SpecialOrders: undefined,
                }
              : undefined,
            SaleLines: saleLines.length > 0 ? { SaleLine: saleLines } : undefined,
            m: workorderData?.m
              ? {
                  itemFeesSubtotal: workorderData.m.itemFeesSubtotal ?? '0',
                  tax: workorderData.m.tax ?? '0',
                }
              : undefined,
            TaxClassTotals:
              saleLines.length > 0
                ? {
                    Tax: {
                      id: '1',
                      name: 'Tax',
                      taxable: (
                        parseFloat(workorderData?.m?.parts ?? '0') +
                        parseFloat(workorderData?.m?.labor ?? '0')
                      ).toFixed(2),
                      rate:
                        workorderData?.m?.tax &&
                        (workorderData?.m?.parts || workorderData?.m?.labor)
                          ? String(
                              (parseFloat(workorderData.m.tax) /
                                (parseFloat(workorderData.m.parts ?? '0') +
                                  parseFloat(workorderData.m.labor ?? '0'))) *
                                100
                            )
                          : '0',
                      amount: workorderData?.m?.tax ?? '0',
                      taxname: 'Tax',
                      subtotal: (
                        parseFloat(workorderData?.m?.parts ?? '0') +
                        parseFloat(workorderData?.m?.labor ?? '0')
                      ).toFixed(2),
                      rate2: '0',
                      amount2: '0',
                      taxname2: '',
                      subtotal2: '0',
                    },
                  }
                : undefined,
            taxTotal: workorderData?.m?.tax ?? '0',
            Shop: workorderData?.Shop,
          },
        };
        setAggregateWorkorder(aggregateWorkorderData);
      })
      .catch((err) => {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        datadogRum.addError(e, {
          message: `Failed to fetch aggregate workorder for workorderID: ${workorderID} and accountID: ${accountID}`,
          accountID,
          workorderID,
        });
      })
      .finally(() => setLoading(false));
  }, [accountID, workorderID]);

  return { aggregateWorkorder, loading, error };
}
